import { z } from "zod";

import {
  formulaTransformSchema,
  plotYAxisSchema,
} from "@/shared/state/visualization/schema/v1";
import {
  meshSchema,
  pathSchema,
  pointCloudSchema,
  positionBasePresetSchema,
  positionTransformationSchema,
  rotationSchema,
  vectorFieldSchema,
} from "@/shared/state/visualization/schema/v1/three-d";

import type { RuleStyle } from "../LayoutRecord";

// Mirrors ThreeDPositionDataMapping in ../LayoutRecord; that type documents
// what topicDataIndex and scale mean.
const threeDPositionDataMappingSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("topicData"),
    scale: z.number(),
    topicDataIndex: z.number(),
  }),
  z.object({
    type: z.literal("fixed"),
    scale: z.number(),
    value: z.number(),
  }),
]);

const threeDPositionStyleSchema = z.object({
  transformation: positionTransformationSchema.optional(),
  basePreset: positionBasePresetSchema.optional(),
  forward: threeDPositionDataMappingSchema,
  right: threeDPositionDataMappingSchema,
  up: threeDPositionDataMappingSchema,
});

/** The per-line fields a plot series, or one member of a group, stores. */
const plotSeriesStyleFields = {
  color: z.string().optional(),
  lineWidth: z.number().optional(),
  pointRadius: z.number().optional(),
  yAxis: plotYAxisSchema.optional(),
  label: z.string().optional(),
  transform: formulaTransformSchema.optional(),
};

/**
 * How a rule's matched data is drawn. One style serves every panel type, and a
 * panel reads only the fields it uses: a plot panel uses every field except
 * threeDComponents; a map panel uses color and label; a 3D panel uses
 * threeDComponents; log, image, document, and raw-message panels ignore the
 * style.
 *
 * Unknown keys pass validation instead of failing it, so a layout saved by a
 * newer application bundle still loads here.
 */
export const ruleStyleSchema: z.ZodType<RuleStyle> = z.object({
  ...plotSeriesStyleFields,
  // Listed so a malformed member list fails validation.
  members: z
    .array(
      z.object({
        index: z.number().int().nonnegative(),
        style: z.object(plotSeriesStyleFields).optional(),
      }),
    )
    .optional(),
  // Every field a 3D entity carries is listed so a malformed one fails
  // validation. A stored layout loads as stored (`LayoutDefinition.fromObj`
  // keeps the object it validated, not the parsed copy), so an unlisted field
  // is kept but never checked.
  threeDComponents: z
    .object({
      entityId: z.string(),
      position: threeDPositionStyleSchema.optional(),
      // Stored whole rather than reduced to a data mapping the way `position`
      // is: a rotation's bound channels hold whole TopicData objects, as a
      // mesh's `jointValues` and a vector field's axes do. Loading swaps each
      // for the topic its rule matched by name and path (`rebindTopicData`),
      // so the ids stored here are never read back.
      rotation: rotationSchema.optional(),
      mesh: meshSchema.optional(),
      path: pathSchema.optional(),
      vectorField: vectorFieldSchema.optional(),
      // Verbatim, like `vectorField`: the cloud's `source` is a whole TopicData,
      // which loading replaces with the topic at `pointCloudTopicDataIndex`.
      pointCloud: pointCloudSchema.optional(),
      // A sibling of `pointCloud` rather than a key inside it: that schema is
      // strict, so a bundle without the key would reject the whole layout
      // instead of stripping it.
      pointCloudTopicDataIndex: z.number().optional(),
    })
    .optional(),
});
